import type { GameState, GameStats, GameSettings } from './types';

// Storage keys with namespacing
const STORAGE_KEYS = {
  GAME_STATE: 'riddle-v2:game-state',
  GAME_STATS: 'riddle-v2:game-stats',
  GAME_SETTINGS: 'riddle-v2:settings',
  WINS: 'riddle-v2:wins',
  PLAYED: 'riddle-v2:played',
  WIN_ATTEMPTS: 'riddle-v2:win-attempts',
} as const;

// Default values
const DEFAULT_GAME_STATE: Partial<GameState> = {
  board: Array(4)
    .fill(null)
    .map(() => Array(6).fill('')),
  boardValidationGrid: Array(4)
    .fill(null)
    .map(() => Array(6).fill('')),
  currentAttempt: { attempt: 0, letterPos: 0 },
  wordSet: [],
  correctWord: '',
  question: '',
  disabledLetters: [],
  shakeRow: false,
  gameOver: { gameOver: false, guessedWord: false },
  attempt: {
    try1: '',
    try2: '',
    try3: '',
    try4: '',
  },
  winAttempt: {
    first: 0,
    second: 0,
    third: 0,
    fourth: 0,
    winAlert: '',
  },
  wins: [],
  played: [],
  alertText: '',
  activeAlert: { alert: false },
  mode: 'light',
  contrast: 'nocontrast',
};

const DEFAULT_GAME_STATS: GameStats = {
  gamesPlayed: 0,
  gamesWon: 0,
  currentStreak: 0,
  maxStreak: 0,
  winAttempts: {
    first: 0,
    second: 0,
    third: 0,
    fourth: 0,
  },
  averageGuesses: 0,
};

const DEFAULT_GAME_SETTINGS: GameSettings = {
  theme: 'light',
  contrast: 'nocontrast',
  animationsEnabled: true,
};

// Utility functions
const safeJSONParse = <T>(value: string | null, defaultValue: T): T => {
  if (!value) return defaultValue;
  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn('Failed to parse stored value:', error);
    return defaultValue;
  }
};

const safeJSONStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value);
  } catch (error) {
    console.warn('Failed to stringify value:', error);
    return '{}';
  }
};

// Game State Persistence
export const saveGameState = (state: Partial<GameState>): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.GAME_STATE, safeJSONStringify(state));
  } catch (error) {
    console.error('Failed to save game state:', error);
  }
};

export const loadGameState = (): Partial<GameState> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.GAME_STATE);
    return safeJSONParse(stored, DEFAULT_GAME_STATE);
  } catch (error) {
    console.error('Failed to load game state:', error);
    return DEFAULT_GAME_STATE;
  }
};

export const clearGameState = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.GAME_STATE);
  } catch (error) {
    console.error('Failed to clear game state:', error);
  }
};

// Game Stats Persistence
export const saveGameStats = (stats: GameStats): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.GAME_STATS, safeJSONStringify(stats));
  } catch (error) {
    console.error('Failed to save game stats:', error);
  }
};

export const loadGameStats = (): GameStats => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.GAME_STATS);
    const baseStats = safeJSONParse(stored, DEFAULT_GAME_STATS);
    
    // Load the actual win attempts from separate storage
    const winAttempts = loadWinAttempts();
    
    // Combine the base stats with the actual win attempts
    return {
      ...baseStats,
      winAttempts: {
        first: winAttempts.first,
        second: winAttempts.second,
        third: winAttempts.third,
        fourth: winAttempts.fourth,
      },
    };
  } catch (error) {
    console.error('Failed to load game stats:', error);
    return DEFAULT_GAME_STATS;
  }
};

export const clearGameStats = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.GAME_STATS);
  } catch (error) {
    console.error('Failed to clear game stats:', error);
  }
};

// Game Settings Persistence
export const saveGameSettings = (settings: GameSettings): void => {
  try {
    localStorage.setItem(
      STORAGE_KEYS.GAME_SETTINGS,
      safeJSONStringify(settings)
    );
  } catch (error) {
    console.error('Failed to save game settings:', error);
  }
};

export const loadGameSettings = (): GameSettings => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.GAME_SETTINGS);
    return safeJSONParse(stored, DEFAULT_GAME_SETTINGS);
  } catch (error) {
    console.error('Failed to load game settings:', error);
    return DEFAULT_GAME_SETTINGS;
  }
};

export const clearGameSettings = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.GAME_SETTINGS);
  } catch (error) {
    console.error('Failed to clear game settings:', error);
  }
};

// Wins and Played Games Persistence
export const saveWins = (wins: string[]): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.WINS, safeJSONStringify(wins));
  } catch (error) {
    console.error('Failed to save wins:', error);
  }
};

export const loadWins = (): string[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.WINS);
    return safeJSONParse(stored, []);
  } catch (error) {
    console.error('Failed to load wins:', error);
    return [];
  }
};

export const savePlayed = (played: string[]): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.PLAYED, safeJSONStringify(played));
  } catch (error) {
    console.error('Failed to save played games:', error);
  }
};

export const loadPlayed = (): string[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.PLAYED);
    return safeJSONParse(stored, []);
  } catch (error) {
    console.error('Failed to load played games:', error);
    return [];
  }
};

// Win Attempts Persistence
export const saveWinAttempts = (winAttempts: GameState['winAttempt']): void => {
  try {
    localStorage.setItem(
      STORAGE_KEYS.WIN_ATTEMPTS,
      safeJSONStringify(winAttempts)
    );
  } catch (error) {
    console.error('Failed to save win attempts:', error);
  }
};

export const loadWinAttempts = (): GameState['winAttempt'] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.WIN_ATTEMPTS);
    return safeJSONParse(stored, DEFAULT_GAME_STATE.winAttempt!);
  } catch (error) {
    console.error('Failed to load win attempts:', error);
    return DEFAULT_GAME_STATE.winAttempt!;
  }
};

// Migration and Cleanup
export const migrateOldStorage = (): void => {
  try {
    // Import and run the migration system
    import('@/utils/migration').then(({ runMigrationIfNeeded }) => {
      const migratedData = runMigrationIfNeeded();
      
      // Apply migrated data to current storage
      if (Object.keys(migratedData.gameState).length > 0) {
        saveGameState(migratedData.gameState);
      }
      if (migratedData.wins.length > 0) {
        saveWins(migratedData.wins);
      }
      if (migratedData.played.length > 0) {
        savePlayed(migratedData.played);
      }
      if (migratedData.winAttempts) {
        saveWinAttempts(migratedData.winAttempts);
      }
      if (migratedData.settings) {
        saveGameSettings({
          ...DEFAULT_GAME_SETTINGS,
          ...migratedData.settings,
        });
      }
    });
  } catch (error) {
    console.error('Failed to migrate old storage:', error);
  }
};

export const clearAllGameData = (): void => {
  try {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.error('Failed to clear all game data:', error);
  }
};

// Storage availability check
export const isStorageAvailable = (): boolean => {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};
